import dayjs from "dayjs";
import { getLogger } from "@hoteltradergit/ht-telemetry-ui-lib";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

// A date is "past" when its calendar day is before today's.
//
// ⚠ "Today" here is the USER's calendar day, which is the right question for the
// picker but the wrong one for the search API. A hotel in another zone can already
// be a day ahead, so a check-in that looks like today to the user is yesterday to
// that property and the search returns "Check in date can not be in past".
// getPastCheckInConflicts below answers the per-property question.
const isPastDate = (date) => dayjs(date).isBefore(dayjs(), "day");

export const DEFAULT_FORM_VALUES = {
  selectedProperties: [],
  selectedCity: [],
  dateRanges: [],
  bookDate: null,
  adults: 2,
  children: 0,
  childAges: [],
};

const MAX_CHILD_AGES = 21;

// Helper function to parse bookDate. A stored booking creation date that has
// since fallen into the past is dropped — the picker disables past dates, so a
// restored one could otherwise be searched with even though it can't be picked.
const parseBookDate = (parsed) => {
  if (parsed?.bookDate) {
    const asDate = new Date(parsed.bookDate);
    if (Number.isNaN(asDate.getTime()) || isPastDate(asDate)) {
      parsed.bookDate = null;
      return;
    }
    parsed.bookDate = asDate;
  }
};

// Helper function to parse dateRanges
const parseDateRanges = (parsed) => {
  if (parsed && Array.isArray(parsed.dateRanges)) {
    parsed.dateRanges = parsed.dateRanges.map((range) => {
      if (Array.isArray(range) && range.length === 2) {
        const start = range[0] ? new Date(range[0]) : null;
        const end = range[1] ? new Date(range[1]) : null;

        return [start, end];
      }
      return range;
    });
  }
};

export const persistFormValues = (finalFormValues) => {
  // Persist the latest form values for future sessions
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.setItem(
        "priceCheckFormValues",
        JSON.stringify(finalFormValues)
      );
    } catch (error) {
      // Non-fatal (e.g. quota exceeded, privacy mode) — the value simply
      // isn't persisted. Logged like every other catch in this file so the
      // failure is diagnosable rather than silent.
      getLogger().warn("PriceCheckFormUtils", {
        type: "Warn",
        page_url: window.location.href,
        error_desc: "Error persisting form values to localStorage",
        error_stack: error?.stack ?? null,
      });
    }
  }
};

// Constants for localStorage keys
const STORAGE_KEYS = {
  SEARCH_BASED_PROPERTY_NAMES: "priceCheck_searchBasedPropertyNames",
};

// Helper function to check if localStorage is available
const isLocalStorageAvailable = () =>
  typeof window !== "undefined" && window.localStorage;

// Helper function to safely handle localStorage operations
const safeLocalStorageOperation = (operation, errorMessage) => {
  if (!isLocalStorageAvailable()) return null;

  try {
    return operation();
  } catch (error) {
    getLogger().error("PriceCheckFormUtils", {
      type: "Error",
      page_url: window.location.href,
      error_desc: errorMessage,
      error_stack: error?.stack ?? null,
    });
    return null;
  }
};

export const persistSearchBasedPropertyNames = (propertyNames) => {
  // Early return for invalid input
  if (!Array.isArray(propertyNames)) {
    getLogger().warn("PriceCheckFormUtils", {
      type: "Warn",
      page_url: window.location.href,
      error_desc: "persistSearchBasedPropertyNames: Expected array, got",
      error_stack: (typeof propertyNames)?.stack ?? null,
    });
    return;
  }

  safeLocalStorageOperation(
    () =>
      window.localStorage.setItem(
        STORAGE_KEYS.SEARCH_BASED_PROPERTY_NAMES,
        JSON.stringify(propertyNames)
      ),
    "Error persisting search-based property names to localStorage"
  );
};

// Persist search state (whether to show form or results)
export const persistSearchState = (isEdit, hasSearchResults = false) => {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      const searchState = {
        isEdit,
        hasSearchResults,
        timestamp: Date.now(),
      };
      window.localStorage.setItem(
        "priceCheckSearchState",
        JSON.stringify(searchState)
      );
    } catch (error) {
      getLogger().error("PriceCheckFormUtils", {
        type: "Error",
        page_url: window.location.href,
        error_desc: "Error persisting search state",
        error_stack: error?.stack ?? null,
      });
    }
  }
};

// Get initial search state from localStorage
export const getInitialSearchState = (defaultIsEdit = true) => {
  if (typeof window === "undefined" || !window.localStorage) {
    return { isEdit: defaultIsEdit, hasSearchResults: false };
  }

  try {
    const raw = window.localStorage.getItem("priceCheckSearchState");
    if (!raw) return { isEdit: defaultIsEdit, hasSearchResults: false };

    const parsed = JSON.parse(raw);

    // Check if the stored data is recent (within 24 hours)
    const isRecent =
      parsed.timestamp && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000;

    if (!isRecent) {
      // Clear old data
      window.localStorage.removeItem("priceCheckSearchState");
      return { isEdit: defaultIsEdit, hasSearchResults: false };
    }

    return {
      isEdit: parsed.isEdit ?? defaultIsEdit,
      hasSearchResults: parsed.hasSearchResults ?? false,
    };
  } catch (error) {
    getLogger().error("PriceCheckFormUtils", {
      type: "Error",
      page_url: window.location.href,
      error_desc: "Error parsing search state",
      error_stack: error?.stack ?? null,
    });
    return { isEdit: defaultIsEdit, hasSearchResults: false };
  }
};

// Helper function to get auto-selected properties
const getAutoSelectedProperties = (properties) => {
  return properties?.length === 1 ? [properties[0]?.propertyId] : [];
};

// Helper function to get default values with auto-selection
const getDefaultValuesWithAutoSelection = (properties) => {
  return {
    ...DEFAULT_FORM_VALUES,
    selectedProperties: getAutoSelectedProperties(properties),
  };
};

// Helper function to load and parse stored values
const loadStoredFormValues = () => {
  const raw = window.localStorage.getItem("priceCheckFormValues");
  if (!raw) return null;

  const parsed = JSON.parse(raw);
  parseBookDate(parsed);
  parseDateRanges(parsed);
  return parsed;
};

// Initialize from localStorage with priority over DEFAULT_FORM_VALUES
export const getInitialFormValues = (properties) => {
  const defaultValues = getDefaultValuesWithAutoSelection(properties);

  if (typeof window === "undefined" || !window.localStorage) {
    return defaultValues;
  }

  try {
    const storedValues = loadStoredFormValues();
    if (storedValues) {
      return storedValues;
    }

    // No stored values found, persist defaults
    persistFormValues(defaultValues);
    return defaultValues;
  } catch (error) {
    // Corrupt/unreadable stored values — fall back to defaults, but log so
    // the failure is diagnosable rather than silent.
    getLogger().error("PriceCheckFormUtils", {
      type: "Error",
      page_url: window.location.href,
      error_desc: "Error reading initial form values from localStorage",
      error_stack: error?.stack ?? null,
    });
    return defaultValues;
  }
};

// Validation utilities
/**
 * EO-1137 — the search API rejects any occupancy node whose length of stay exceeds 28 nights
 * ("Occupancy Node [1]: Length of stay is higher than 28"). Each stay-date range is sent as one
 * occupancy node, so the limit applies per range, not to the combined span.
 */
export const MAX_STAY_NIGHTS = 28;

/** Nights between check-in and check-out, which is what the API counts as length of stay. */
const nightsInRange = (range) => {
  if (!Array.isArray(range) || range.length !== 2) return 0;
  const [start, end] = range;
  if (!start || !end) return 0;

  const from = new Date(start).setHours(0, 0, 0, 0);
  const to = new Date(end).setHours(0, 0, 0, 0);
  if (Number.isNaN(from) || Number.isNaN(to)) return 0;

  return Math.round((to - from) / 86400000);
};

/** 1-based positions of the ranges that are too long, so the message can name them. */
export const getOverlongRangeIndexes = (dateRanges) =>
  (dateRanges || [])
    .map((range, index) =>
      nightsInRange(range) > MAX_STAY_NIGHTS ? index + 1 : 0
    )
    .filter(Boolean);

/**
 * EO-1137 — server errors arrive prefixed with the node that failed, e.g.
 * "Occupancy Node [1]: Length of stay is higher than 28". The prefix means nothing to a hotel,
 * so only the part after it is shown. The validation above should stop that particular message
 * ever appearing; this exists for the other failures that share the format.
 */
export const formatSearchErrorMessage = (
  message,
  fallback = "Failed to fetch data"
) => {
  if (typeof message !== "string" || !message.trim()) return fallback;
  const separator = message.indexOf(":");
  const detail =
    separator === -1 ? message.trim() : message.slice(separator + 1).trim();
  return detail || fallback;
};

export const validateFormValues = (formValues) => {
  const errors = {};

  // Validate that either city or property is selected
  const hasCitySelection =
    formValues.selectedCity && formValues.selectedCity.length > 0;
  const hasPropertySelection =
    formValues.selectedProperties && formValues.selectedProperties.length > 0;

  if (!hasCitySelection && !hasPropertySelection) {
    errors.property = true;
    errors.city = true;
  }

  // Validate that stay dates are selected
  if (!formValues.dateRanges || formValues.dateRanges.length === 0) {
    errors.dateRanges = true;
  }

  // Validate each range against the API's length-of-stay limit
  const overlongRanges = getOverlongRangeIndexes(formValues.dateRanges);
  if (overlongRanges.length > 0) {
    // Also flags dateRanges so the picker picks up the existing error styling.
    errors.dateRanges = true;
    errors.dateRangeTooLong = overlongRanges;
  }

  return errors;
};

export const getValidationMessages = (errors) => {
  const messages = [];

  if (errors.property || errors.city) {
    messages.push("Please select either a city or specific properties");
  }

  // dateRanges doubles as "none selected" and "one is too long" — only the first needs this
  // message, or a user with a 40-night range is told to select dates they already selected.
  if (errors.dateRanges && !errors.dateRangeTooLong) {
    messages.push("Please select stay dates");
  }

  if (errors.dateRangeTooLong?.length) {
    const positions = errors.dateRangeTooLong;
    const subject =
      positions.length === 1
        ? `Stay date range ${positions[0]} is`
        : `Stay date ranges ${positions.join(", ")} are`;
    messages.push(
      `${subject} longer than ${MAX_STAY_NIGHTS} nights. Please select a shorter range.`
    );
  }

  return messages;
};

/**
 * Today's calendar date at a property, as YYYY-MM-DD.
 *
 * Blank or unrecognised zones fall back to UTC rather than throwing: the backend treats
 * a property with no timezone as UTC (see PropertyRepository#findIdsWithoutTimezone), and
 * a malformed zone string must not be able to break search outright.
 */
const todayAtProperty = (timeZone) => {
  const zone = (timeZone || "").trim();
  if (!zone) return dayjs.utc().format("YYYY-MM-DD");
  try {
    return dayjs().tz(zone).format("YYYY-MM-DD");
  } catch {
    return dayjs.utc().format("YYYY-MM-DD");
  }
};

/**
 * EO-1137 — check-in dates that are already in the past *for the property*.
 *
 * The picker blocks past dates against the user's own clock, and that is all it can do:
 * dates are chosen before properties are, and a retained search replays dates the user
 * picked on an earlier day. Neither path knows about the hotel's zone. So a user in
 * India on the 17th can send a check-in of the 17th to a hotel in Japan where it is
 * already the 18th, and the search comes back 400.
 *
 * Compares the same string the API is actually given — `dayjs(iso).format("YYYY-MM-DD")`,
 * matching convertISODateToYMDRespectingUserTimezone — against today at each property, so
 * the check can never disagree with the payload it is guarding.
 *
 * @param {Array<object>} selectedProperties full property objects (need propertyName + timeZone)
 * @param {Array<Array>} dateRanges [checkInISO, checkOutISO] pairs
 * @returns {Array<{propertyName: string, timeZone: string, checkInDate: string}>} one per clash
 */
export const getPastCheckInConflicts = (selectedProperties, dateRanges) => {
  const conflicts = [];

  (selectedProperties || []).forEach((property) => {
    if (!property) return;
    const propertyToday = todayAtProperty(property.timeZone);

    (dateRanges || []).forEach((range) => {
      if (!Array.isArray(range) || !range[0]) return;
      const checkInDate = dayjs(range[0]).format("YYYY-MM-DD");
      if (!checkInDate || checkInDate >= propertyToday) return;

      conflicts.push({
        propertyName:
          property.propertyName || `Property ${property.propertyId}`,
        timeZone: (property.timeZone || "").trim() || "UTC",
        checkInDate,
      });
    });
  });

  return conflicts;
};

/**
 * One line per clash, naming the property and the date — with several properties selected
 * a single "check in date is in the past" tells the user nothing about which hotel to fix.
 */
export const getPastCheckInMessages = (conflicts) =>
  (conflicts || []).map(
    ({ propertyName, checkInDate, timeZone }) =>
      `${propertyName}: ${checkInDate} is already in the past there (${timeZone}). Please choose a later arrival date.`
  );

/**
 * EO-1137 — a booking creation date already in the past *for the property*.
 *
 * Same problem as the check-in above, same blind spot: the Booking Creation Date picker
 * disables past dates and parseBookDate drops a stored one that has since fallen into
 * the past, but both ask whether it is past for the USER. A booking date that is still
 * today in India is yesterday for a hotel in Tokyo, and the search rejects it.
 *
 * Kept separate from getPastCheckInConflicts rather than folded in: bookDate is a single
 * value, not a [start, end] pair, and the advice a user needs is different. The timezone
 * logic itself is shared through todayAtProperty.
 *
 * Formatted exactly as the payload is — usePriceCheckSearch sends
 * convertISODateToYMDRespectingUserTimezone(new Date(bookDate)), which is this same
 * dayjs(...).format("YYYY-MM-DD") — so the guard cannot disagree with what it guards.
 *
 * @param {Array<object>} selectedProperties full property objects (need propertyName + timeZone)
 * @param {string|Date|null} bookDate the booking creation date, or null when unset
 * @returns {Array<{propertyName: string, timeZone: string, bookDate: string}>} one per clash
 */
export const getPastBookDateConflicts = (selectedProperties, bookDate) => {
  if (!bookDate) return [];

  const bookDateYMD = dayjs(bookDate).format("YYYY-MM-DD");
  if (!bookDateYMD || bookDateYMD === "Invalid Date") return [];

  const conflicts = [];

  (selectedProperties || []).forEach((property) => {
    if (!property) return;
    if (bookDateYMD >= todayAtProperty(property.timeZone)) return;

    conflicts.push({
      propertyName: property.propertyName || `Property ${property.propertyId}`,
      timeZone: (property.timeZone || "").trim() || "UTC",
      bookDate: bookDateYMD,
    });
  });

  return conflicts;
};

/** One line per clash, naming the property so the user knows which one to fix. */
export const getPastBookDateMessages = (conflicts) =>
  (conflicts || []).map(
    ({ propertyName, bookDate, timeZone }) =>
      `${propertyName}: the booking creation date ${bookDate} is already in the past there (${timeZone}). Please choose a later date.`
  );

export const clearValidationErrors = (
  formValues,
  changedField,
  currentErrors
) => {
  const errorsToClear = {};

  // Clear property/city errors if either is selected
  if (
    changedField === "selectedProperties" ||
    changedField === "selectedCity"
  ) {
    const hasPropertySelection =
      formValues.selectedProperties && formValues.selectedProperties.length > 0;
    const hasCitySelection =
      formValues.selectedCity && formValues.selectedCity.length > 0;

    if (hasPropertySelection || hasCitySelection) {
      errorsToClear.property = false;
      errorsToClear.city = false;
    }
  }

  // Clear date ranges error if dates are selected
  if (changedField === "dateRanges") {
    const hasDateRanges =
      formValues.dateRanges && formValues.dateRanges.length > 0;
    // Only clear once the ranges are actually valid — clearing on any edit would drop the
    // highlight while a range is still over the limit.
    const stillTooLong =
      getOverlongRangeIndexes(formValues.dateRanges).length > 0;
    if (hasDateRanges && !stillTooLong) {
      errorsToClear.dateRanges = false;
      errorsToClear.dateRangeTooLong = undefined;
    }
  }

  // Only return errors to clear if there are any
  return Object.keys(errorsToClear).length > 0 ? errorsToClear : null;
};

// Form utilities : Age option will be 0 to MAX_CHILD_AGES ie. 21
export const generateAgeOptions = () => {
  return Array.from({ length: MAX_CHILD_AGES + 1 }, (_, i) => ({
    label: i.toString(),
    value: i,
  }));
};

export const generateCityOptions = (properties) => {
  const cityList = properties.map((property) => property.city);
  const uniqueCities = [...new Set(cityList)];
  return uniqueCities.map((city) => ({
    label: city,
    value: city,
  }));
};

export const updateChildAges = (currentChildAges, childrenCount) => {
  const safeCurrentAges = currentChildAges || [];
  return Array.from(
    { length: childrenCount },
    (_, i) => safeCurrentAges[i] || 0
  );
};
